import { Component, computed, effect, inject, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { OfflineService } from '@core/offline/offline.service';

/** How long "Back online" stays after the connection returns. */
const BACK_MS = 3000;

/**
 * A pill at the top while there is no connection: what is on screen was saved
 * on this device (and when), and changes wait for the connection. When it
 * returns, "Back online" for a moment.
 */
@Component({
  selector: 'app-offline-banner',
  standalone: true,
  imports: [DatePipe, MatIconModule, TranslateModule],
  templateUrl: './offline-banner.component.html',
  styleUrls: ['./offline-banner.component.scss'],
})
export class OfflineBannerComponent {
  private readonly offlineService = inject(OfflineService);
  readonly offline = this.offlineService.offline;
  readonly savedSince = this.offlineService.showingSavedSince;
  readonly back = signal(false);
  readonly visible = computed(() => this.offline() || this.back());

  private wasOffline = false;
  private backTimer: ReturnType<typeof setTimeout> | undefined;

  constructor() {
    effect(() => {
      const offline = this.offline();
      if (offline) {
        clearTimeout(this.backTimer);
        this.back.set(false);
      } else if (this.wasOffline) {
        this.back.set(true);
        this.backTimer = setTimeout(() => this.back.set(false), BACK_MS);
      }
      this.wasOffline = offline;
    });
  }
}
